import { makeDictionary, mergeMask, check, createPolicies, arrayFilter, advancedRights, arrayMap } from 'lib/policy';

import { selector } from 'lib/selectors';
import * as env from 'domain/env/envSelector';
import * as companies from 'domain/companies/companiesSelector';
import * as documents from 'domain/documents/documentSelector';
import * as bankFeed from 'domain/bankFeed/selectors';
import {
  hasNonDisconnectedPaymentProviderSelector,
  hasConnectedPaymentProviderSelector,
} from 'domain/paySettings/selectorForPermissions';
import CONST from 'domain/documents/constants';

import { ROLES } from 'domain/env/helpers';

const RIGHTS = {
  IS_AUTHORIZED: 'IS_AUTHORIZED',
  IS_NOT_AUTHORIZED: 'IS_NOT_AUTHORIZED',
  IS_ACCOUNT: 'IS_ACCOUNT',
  IS_LIMITED_ACCOUNT: 'IS_LIMITED_ACCOUNT',
  IS_RESTRICTED_ACCOUNTANT: 'IS_RESTRICTED_ACCOUNTANT',
  IS_NOT_RESTRICTED_ACCOUNTANT: 'IS_NOT_RESTRICTED_ACCOUNTANT',
  IS_READ_ONLY_ACCOUNTANT: 'IS_READ_ONLY_ACCOUNTANT',
  IS_USER: 'IS_USER',
  IS_NOT_USER: 'IS_NOT_USER',
  IS_NOT_CONFIDENTIAL_USER: 'IS_NOT_CONFIDENTIAL_USER',
  IS_CONFIDENTIAL_USER: 'IS_CONFIDENTIAL_USER',
  IS_RESTRICTED_USER: 'IS_RESTRICTED_USER',
  IS_NOT_RESTRICTED_USER: 'IS_NOT_RESTRICTED_USER',
  IS_CONFIDENTIAL_RESTRICTED_USER: 'IS_CONFIDENTIAL_RESTRICTED_USER',
  IS_NOT_CONFIDENTIAL_RESTRICTED_USER: 'IS_NOT_CONFIDENTIAL_RESTRICTED_USER',
  IS_SUPPLIER_USER: 'IS_SUPPLIER_USER',
  IS_NOT_SUPPLIER_USER: 'IS_NOT_SUPPLIER_USER',
  IS_COMPANY_SUPPORT_GENERAL_DOC_ONLY: 'IS_COMPANY_SUPPORT_GENERAL_DOC_ONLY',
  IS_COMPANY_SUPPORT_FINANCIAL_DOC: 'IS_COMPANY_SUPPORT_FINANCIAL_DOC',
  IS_ADMIN: 'IS_ADMIN',
  IS_NOT_ADMIN: 'IS_NOT_ADMIN',
  IS_EXPIRED: 'IS_EXPIRED',
  IS_CURRENT_DOCUMENT_FINANCIAL: 'IS_CURRENT_DOCUMENT_FINANCIAL',
  IS_ACCEPTED_DOCUMENT: 'IS_ACCEPTED_DOCUMENT',
  IS_NOT_ACCEPTED_DOCUMENT: 'IS_NOT_ACCEPTED_DOCUMENT',
  IS_DOCUMENT_BEING_PROCESSED: 'IS_DOCUMENT_BEING_PROCESSED',
  IS_NOT_DOCUMENT_BEING_PROCESSED: 'IS_NOT_DOCUMENT_BEING_PROCESSED',
  IS_DOCUMENT_SCHEDULED_ACCEPTANCE: 'IS_DOCUMENT_SCHEDULED_ACCEPTANCE',
  ALLOW_SUPPLIER_FORM: 'ALLOW_SUPPLIER_FORM',
  ALLOW_FRESHDESK: 'ALLOW_FRESHDESK',
  ALLOW_APPROVALS_GROUPS: 'ALLOW_APPROVALS_GROUPS',
  ALLOW_DASHBOARD_ACCESS: 'ALLOW_DASHBOARD_ACCESS',
  ONLY_ADMIN_CAN_CHANGE_AF: 'ONLY_ADMIN_CAN_CHANGE_AF',
  NOT_ONLY_ADMIN_CAN_CHANGE_AF: 'NOT_ONLY_ADMIN_CAN_CHANGE_AF',
  BANK_FEED_CAN_MANAGE_CONNECTION: 'BANK_FEED_CAN_MANAGE_CONNECTION',
  BANK_FEED_CAN_VIEW_CONNECTION: 'BANK_FEED_CAN_VIEW_CONNECTION',
  BANK_FEED_CAN_SYNC_TRANSACTION: 'BANK_FEED_CAN_SYNC_TRANSACTION',
  BANK_FEED_CAN_VIEW_TRANSACTION: 'BANK_FEED_CAN_VIEW_TRANSACTION',
  IS_PAY_MODULE_AVAILABLE_FOR_THIS_COMPANY: 'IS_PAY_MODULE_AVAILABLE_FOR_THIS_COMPANY',
  CAN_PREPARE_PAYMENT_FOR_NON_PUBLISHED_INVOICES: 'CAN_PREPARE_PAYMENT_FOR_NON_PUBLISHED_INVOICES',
  PAY_IS_OWNER: 'PAY_IS_OWNER',
  PAY_IS_NOT_OWNER: 'PAY_IS_NOT_OWNER',
  PAY_IS_CONTROLLER: 'PAY_IS_CONTROLLER',
  PAY_IS_NOT_CONTROLLER: 'PAY_IS_NOT_CONTROLLER',
  PAY_IS_APPROVER: 'PAY_IS_APPROVER',
  PAY_IS_NOT_APPROVER: 'PAY_IS_NOT_APPROVER',
  PAY_HAS_NON_DISCONNECTED_PAYMENT_PROVIDER: 'PAY_HAS_NON_DISCONNECTED_PAYMENT_PROVIDER',
  PAY_ALL_DISCONNECTED_PAYMENT_PROVIDER: 'PAY_ALL_DISCONNECTED_PAYMENT_PROVIDER',
  PAY_HAS_CONNECTED_PAYMENT_PROVIDER: 'PAY_HAS_CONNECTED_PAYMENT_PROVIDER',
};

const dictionary = makeDictionary(Object.keys(RIGHTS));

export const myRestriction = selector(
  env.roleSelector,
  env.roleModificatorsSelector,
  companies.doesCurrentCompanySupportGeneralDocsOnlySelector,
  env.isAdminSelector,
  env.isExpiredSelector,
  documents.documentSelector,
  companies.companyGeneralFeatureFlagsSelector,
  env.isAllowFreshdeskSelector,
  companies.companyDynamicFeaturesSelector,
  bankFeed.bankFeedUserPermissionsSelector,
  documents.currentCompanyIdSelector,
  env.payModuleCompaniesContextSelector,
  env.payModuleCompanyPermissionsSelector,
  hasNonDisconnectedPaymentProviderSelector,
  hasConnectedPaymentProviderSelector,
  (
    e,
    roleModificators,
    dccsgdo,
    isAdmin,
    isExpired,
    currentDocument,
    companyFeatures,
    allowFreshdesk,
    companyDynamicFeatures,
    bankFeedPermissions,
    companyId,
    payModuleCompaniesContext,
    payModuleCompanyPermissions,
    hasNonDisconnectedPaymentProvider,
    hasConnectedPaymentProvider,
  ) => {
    return mergeMask(dictionary)(
      []
        .concat([e ? RIGHTS.IS_AUTHORIZED : RIGHTS.IS_NOT_AUTHORIZED])
        .concat(e === ROLES.accountant ? [RIGHTS.IS_ACCOUNT] : [])
        .concat(e === ROLES.accountant && roleModificators.limited ? [RIGHTS.IS_LIMITED_ACCOUNT] : [])
        .concat(
          e === ROLES.accountant && roleModificators.restricted
            ? [RIGHTS.IS_RESTRICTED_ACCOUNTANT]
            : [RIGHTS.IS_NOT_RESTRICTED_ACCOUNTANT],
        )
        .concat(e === ROLES.accountant && roleModificators.readonly ? [RIGHTS.IS_READ_ONLY_ACCOUNTANT] : [])
        .concat(e === ROLES.user ? [RIGHTS.IS_USER] : [RIGHTS.IS_NOT_USER])
        .concat(e === ROLES['confidential-user'] ? [RIGHTS.IS_CONFIDENTIAL_USER] : [RIGHTS.IS_NOT_CONFIDENTIAL_USER])
        .concat(
          e === ROLES.user && roleModificators.restricted
            ? [RIGHTS.IS_RESTRICTED_USER]
            : [RIGHTS.IS_NOT_RESTRICTED_USER],
        )
        .concat(
          e === ROLES['confidential-user'] && roleModificators.restricted
            ? [RIGHTS.IS_CONFIDENTIAL_RESTRICTED_USER]
            : [RIGHTS.IS_NOT_CONFIDENTIAL_RESTRICTED_USER],
        )

        .concat(e === ROLES.supplier ? [RIGHTS.IS_SUPPLIER_USER] : [RIGHTS.IS_NOT_SUPPLIER_USER])
        .concat([dccsgdo ? RIGHTS.IS_COMPANY_SUPPORT_GENERAL_DOC_ONLY : RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC])
        .concat(isAdmin ? [RIGHTS.IS_ADMIN] : [RIGHTS.IS_NOT_ADMIN])
        .concat(isExpired ? [RIGHTS.IS_EXPIRED] : [])
        .concat(currentDocument.get('doctype') === 'financial' ? [RIGHTS.IS_CURRENT_DOCUMENT_FINANCIAL] : [])
        .concat(
          currentDocument.tags.has(CONST.tags.isAccepted)
            ? [RIGHTS.IS_ACCEPTED_DOCUMENT]
            : [RIGHTS.IS_NOT_ACCEPTED_DOCUMENT],
        )
        .concat(
          currentDocument.tags.has(CONST.tags.isBeingProcessed)
            ? [RIGHTS.IS_DOCUMENT_BEING_PROCESSED]
            : [RIGHTS.IS_NOT_DOCUMENT_BEING_PROCESSED],
        )
        .concat(
          currentDocument.tags.has(CONST.tags.isScheduledAcceptance) ? [RIGHTS.IS_DOCUMENT_SCHEDULED_ACCEPTANCE] : [],
        )
        .concat(companyFeatures.get('supplier_form') ? [RIGHTS.ALLOW_SUPPLIER_FORM] : [])
        .concat(companyFeatures.get('approvals') ? [RIGHTS.ALLOW_APPROVALS_GROUPS] : [])
        .concat(
          companyFeatures.get('only_admin_can_change_af', false)
            ? [RIGHTS.ONLY_ADMIN_CAN_CHANGE_AF]
            : [RIGHTS.NOT_ONLY_ADMIN_CAN_CHANGE_AF],
        )
        .concat(allowFreshdesk ? [RIGHTS.ALLOW_FRESHDESK] : []) // this should remain in rights
        .concat(companyDynamicFeatures.get('support_vendor_expenses', false) ? [RIGHTS.ALLOW_DASHBOARD_ACCESS] : [])
        .concat(bankFeedPermissions.get('connectionManage', false) ? [RIGHTS.BANK_FEED_CAN_MANAGE_CONNECTION] : [])
        .concat(bankFeedPermissions.get('connectionView', false) ? [RIGHTS.BANK_FEED_CAN_VIEW_CONNECTION] : [])
        .concat(bankFeedPermissions.get('transactionSync', false) ? [RIGHTS.BANK_FEED_CAN_SYNC_TRANSACTION] : [])
        .concat(bankFeedPermissions.get('transactionView', false) ? [RIGHTS.BANK_FEED_CAN_VIEW_TRANSACTION] : [])
        .concat(
          companyId && payModuleCompaniesContext.includes(companyId)
            ? [RIGHTS.IS_PAY_MODULE_AVAILABLE_FOR_THIS_COMPANY]
            : [],
        )
        .concat(
          companyFeatures.get('enable_prepare_payment_for_non_published_invoices', false)
            ? [RIGHTS.CAN_PREPARE_PAYMENT_FOR_NON_PUBLISHED_INVOICES]
            : [],
        )
        // TODO: When there are multiple payment providers, refactor this to depend on the selected payment provider
        .concat(payModuleCompanyPermissions.first()?.get('isOwner') ? [RIGHTS.PAY_IS_OWNER] : [RIGHTS.PAY_IS_NOT_OWNER])
        .concat(
          payModuleCompanyPermissions.first()?.get('isController')
            ? [RIGHTS.PAY_IS_CONTROLLER]
            : [RIGHTS.PAY_IS_NOT_CONTROLLER],
        )
        .concat(
          payModuleCompanyPermissions.first()?.get('isApprover')
            ? [RIGHTS.PAY_IS_APPROVER]
            : [RIGHTS.PAY_IS_NOT_APPROVER],
        )
        .concat(
          hasNonDisconnectedPaymentProvider
            ? [RIGHTS.PAY_HAS_NON_DISCONNECTED_PAYMENT_PROVIDER]
            : [RIGHTS.PAY_ALL_DISCONNECTED_PAYMENT_PROVIDER],
        )
        .concat(hasConnectedPaymentProvider ? [RIGHTS.PAY_HAS_CONNECTED_PAYMENT_PROVIDER] : []),
    );
  },
);

const policy = (...arr) => createPolicies(dictionary, arr);

export const isGranted = selector(myRestriction, (restriction) => (lvl) => check(lvl, restriction));

export { arrayFilter, makeDictionary, mergeMask, advancedRights, check, arrayMap };

export const IS_AUTHORIZED = policy(RIGHTS.IS_AUTHORIZED);
export const IS_AUTHORIZED_AND_NOT_SUPPLIER = policy(
  RIGHTS.IS_USER,
  RIGHTS.IS_RESTRICTED_USER,
  RIGHTS.IS_CONFIDENTIAL_USER,
  RIGHTS.IS_CONFIDENTIAL_RESTRICTED_USER,
  RIGHTS.IS_ACCOUNT,
  RIGHTS.IS_LIMITED_ACCOUNT,
  RIGHTS.IS_RESTRICTED_ACCOUNTANT,
  RIGHTS.IS_READ_ONLY_ACCOUNTANT,
  RIGHTS.IS_ADMIN,
);

export const IS_CONFIDENTIAL_USER = policy(RIGHTS.IS_CONFIDENTIAL_USER, RIGHTS.IS_CONFIDENTIAL_RESTRICTED_USER);
export const IS_RESTRICTED_USER = policy(RIGHTS.IS_RESTRICTED_USER);
export const IS_CONFIDENTIAL_RESTRICTED_USER = policy(RIGHTS.IS_CONFIDENTIAL_RESTRICTED_USER);
export const IS_SUPPLIER_USER = policy(RIGHTS.IS_SUPPLIER_USER);
export const IS_NOT_AUTHORIZED = policy(RIGHTS.IS_NOT_AUTHORIZED, RIGHTS.IS_EXPIRED);
export const IS_ACCOUNT = policy(
  RIGHTS.IS_ACCOUNT,
  RIGHTS.IS_LIMITED_ACCOUNT,
  RIGHTS.IS_RESTRICTED_ACCOUNTANT,
  RIGHTS.IS_READ_ONLY_ACCOUNTANT,
);
export const IS_LIMITED_ACCOUNT = policy(RIGHTS.IS_LIMITED_ACCOUNT);
export const IS_RESTRICTED_ACCOUNTANT = policy(RIGHTS.IS_RESTRICTED_ACCOUNTANT);
export const IS_READ_ONLY_ACCOUNTANT = policy(RIGHTS.IS_READ_ONLY_ACCOUNTANT);
export const IS_USER = policy(
  RIGHTS.IS_USER,
  RIGHTS.IS_RESTRICTED_USER,
  RIGHTS.IS_CONFIDENTIAL_USER,
  RIGHTS.IS_CONFIDENTIAL_RESTRICTED_USER,
  RIGHTS.IS_SUPPLIER_USER,
);
export const IS_CONFIDENTIAL_GRANTED_USER = policy(
  RIGHTS.IS_CONFIDENTIAL_USER,
  RIGHTS.IS_CONFIDENTIAL_RESTRICTED_USER,
  RIGHTS.IS_ACCOUNT,
  RIGHTS.IS_LIMITED_ACCOUNT,
  RIGHTS.IS_RESTRICTED_ACCOUNTANT,
  RIGHTS.IS_READ_ONLY_ACCOUNTANT,
);

export const IS_BOOKKEEPER_IN_FINANCIAL_COMPANY = policy(
  [RIGHTS.IS_ACCOUNT, RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC],
  [RIGHTS.IS_LIMITED_ACCOUNT, RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC],
  [RIGHTS.IS_RESTRICTED_ACCOUNTANT, RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC],
  [RIGHTS.IS_READ_ONLY_ACCOUNTANT, RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC],
);
export const IS_BK_WITHOUT_ADMIN_PERMISSIONS = policy(
  [RIGHTS.IS_ACCOUNT, RIGHTS.IS_NOT_ADMIN],
  [RIGHTS.IS_LIMITED_ACCOUNT, RIGHTS.IS_NOT_ADMIN],
  [RIGHTS.IS_RESTRICTED_ACCOUNTANT, RIGHTS.IS_NOT_ADMIN],
  [RIGHTS.IS_READ_ONLY_ACCOUNTANT, RIGHTS.IS_NOT_ADMIN],
);
export const IS_ALLOW_SUPPLIER_FORM_FOR_BK_WITHOUT_ADMIN_PERMISSIONS = policy(
  [RIGHTS.IS_ACCOUNT, RIGHTS.IS_NOT_ADMIN, RIGHTS.ALLOW_SUPPLIER_FORM],
  [RIGHTS.IS_LIMITED_ACCOUNT, RIGHTS.IS_NOT_ADMIN, RIGHTS.ALLOW_SUPPLIER_FORM],
  [RIGHTS.IS_RESTRICTED_ACCOUNTANT, RIGHTS.IS_NOT_ADMIN, RIGHTS.ALLOW_SUPPLIER_FORM],
  [RIGHTS.IS_READ_ONLY_ACCOUNTANT, RIGHTS.IS_NOT_ADMIN, RIGHTS.ALLOW_SUPPLIER_FORM],
);
export const ALLOW_APPROVAL_GROUPS_FOR_BK = policy(
  [RIGHTS.IS_ACCOUNT, RIGHTS.ALLOW_APPROVALS_GROUPS],
  [RIGHTS.IS_LIMITED_ACCOUNT, RIGHTS.ALLOW_APPROVALS_GROUPS],
  [RIGHTS.IS_RESTRICTED_ACCOUNTANT, RIGHTS.ALLOW_APPROVALS_GROUPS],
  [RIGHTS.IS_READ_ONLY_ACCOUNTANT, RIGHTS.ALLOW_APPROVALS_GROUPS],
);
export const ALLOW_SUPPLIER_FORM = policy([RIGHTS.ALLOW_SUPPLIER_FORM]);
export const ALLOW_APPROVALS_GROUPS = policy([RIGHTS.ALLOW_APPROVALS_GROUPS]);
export const IS_COMPANY_SUPPORT_FINANCIAL_DOC = policy(RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC);
export const IS_ADMIN = policy(RIGHTS.IS_ADMIN);
export const IS_EXPIRED = policy(RIGHTS.IS_EXPIRED);
export const IS_DOCUMENT_SCHEDULED_ACCEPTANCE = policy(RIGHTS.IS_DOCUMENT_SCHEDULED_ACCEPTANCE);
export const IS_WORK_WITH_FINANCIAL_DOCUMENT = policy([
  RIGHTS.IS_NOT_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_USER,
  RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC,
]);

export const IS_ALLOW_MERGE_DOCUMENTS = policy(
  RIGHTS.IS_USER,
  RIGHTS.IS_RESTRICTED_USER,
  RIGHTS.IS_CONFIDENTIAL_USER,
  RIGHTS.IS_CONFIDENTIAL_RESTRICTED_USER,
  RIGHTS.IS_ACCOUNT,
  RIGHTS.IS_LIMITED_ACCOUNT,
  RIGHTS.IS_RESTRICTED_ACCOUNTANT,
  RIGHTS.IS_READ_ONLY_ACCOUNTANT,
);

export const IS_WORK_ON_WITH_CURRENT_DOCUMENT_AS_FINANCIAL = policy([
  RIGHTS.IS_CURRENT_DOCUMENT_FINANCIAL,
  RIGHTS.IS_NOT_USER,
  RIGHTS.IS_NOT_RESTRICTED_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_RESTRICTED_USER,
]);

export const CAN_CHECK_DUPLICATE_ERP_DOCS = policy([RIGHTS.IS_CURRENT_DOCUMENT_FINANCIAL, RIGHTS.IS_NOT_SUPPLIER_USER]);

export const IS_ACCEPTABLE_DOCUMENT = policy([
  RIGHTS.IS_CURRENT_DOCUMENT_FINANCIAL,
  RIGHTS.IS_NOT_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_USER,
  RIGHTS.IS_NOT_RESTRICTED_USER,
  RIGHTS.IS_NOT_SUPPLIER_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_RESTRICTED_USER,
  RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC,
  RIGHTS.IS_NOT_ACCEPTED_DOCUMENT,
  RIGHTS.IS_NOT_DOCUMENT_BEING_PROCESSED,
]);

export const ALLOW_FRESHDESK = policy(RIGHTS.ALLOW_FRESHDESK);

export const ALLOW_DASHBOARD_ACCESS = policy([
  RIGHTS.IS_AUTHORIZED,
  RIGHTS.IS_NOT_RESTRICTED_ACCOUNTANT,
  RIGHTS.IS_NOT_RESTRICTED_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_RESTRICTED_USER,
  RIGHTS.ALLOW_DASHBOARD_ACCESS,
]);

export const CAN_MANAGE_BULK_AF = policy(
  [RIGHTS.IS_ADMIN, RIGHTS.ONLY_ADMIN_CAN_CHANGE_AF],
  [RIGHTS.IS_AUTHORIZED, RIGHTS.NOT_ONLY_ADMIN_CAN_CHANGE_AF],
);

export const CAN_MANAGE_APPROVALS_SETTINGS = policy(
  [RIGHTS.IS_ADMIN, RIGHTS.ONLY_ADMIN_CAN_CHANGE_AF],
  [RIGHTS.IS_ACCOUNT, RIGHTS.NOT_ONLY_ADMIN_CAN_CHANGE_AF],
);

export const BANK_FEED_CAN_MANAGE_CONNECTION = policy(RIGHTS.BANK_FEED_CAN_MANAGE_CONNECTION);
export const BANK_FEED_CAN_VIEW_CONNECTION = policy(RIGHTS.BANK_FEED_CAN_VIEW_CONNECTION);
export const BANK_FEED_CAN_SYNC_TRANSACTION = policy(RIGHTS.BANK_FEED_CAN_SYNC_TRANSACTION);
export const BANK_FEED_CAN_VIEW_TRANSACTION = policy(RIGHTS.BANK_FEED_CAN_VIEW_TRANSACTION);
export const BANK_FEED_CAN_VIEW_DATA_SYNC = policy(
  [RIGHTS.BANK_FEED_CAN_VIEW_TRANSACTION],
  [RIGHTS.BANK_FEED_CAN_VIEW_CONNECTION],
);

export const IS_PAY_MODULE_AVAILABLE_FOR_THIS_COMPANY = policy(RIGHTS.IS_PAY_MODULE_AVAILABLE_FOR_THIS_COMPANY);

export const CAN_SEND_TO_DOKKA_PAY = policy([
  RIGHTS.IS_NOT_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_USER,
  RIGHTS.IS_NOT_RESTRICTED_USER,
  RIGHTS.IS_NOT_SUPPLIER_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_RESTRICTED_USER,
  RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC,
  RIGHTS.IS_PAY_MODULE_AVAILABLE_FOR_THIS_COMPANY,
]);

const CAN_PREPARE_PAYMENT_BASE_RIGHTS = [
  RIGHTS.IS_CURRENT_DOCUMENT_FINANCIAL,
  RIGHTS.IS_NOT_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_USER,
  RIGHTS.IS_NOT_RESTRICTED_USER,
  RIGHTS.IS_NOT_SUPPLIER_USER,
  RIGHTS.IS_NOT_CONFIDENTIAL_RESTRICTED_USER,
  RIGHTS.IS_COMPANY_SUPPORT_FINANCIAL_DOC,
  RIGHTS.IS_NOT_DOCUMENT_BEING_PROCESSED,
  RIGHTS.IS_PAY_MODULE_AVAILABLE_FOR_THIS_COMPANY,
];

// Show "Prepare payment" for accepted (published) documents, OR for non-published
// documents when the company enables `enable_prepare_payment_for_non_published_invoices`.
export const CAN_PREPARE_PAYMENT = policy(
  [...CAN_PREPARE_PAYMENT_BASE_RIGHTS, RIGHTS.IS_ACCEPTED_DOCUMENT],
  [...CAN_PREPARE_PAYMENT_BASE_RIGHTS, RIGHTS.CAN_PREPARE_PAYMENT_FOR_NON_PUBLISHED_INVOICES],
);

export const PAY_IS_OWNER = policy(RIGHTS.PAY_IS_OWNER);
export const PAY_IS_NOT_OWNER = policy(RIGHTS.PAY_IS_NOT_OWNER);
export const PAY_IS_CONTROLLER = policy(RIGHTS.PAY_IS_CONTROLLER);
export const PAY_IS_NOT_CONTROLLER = policy(RIGHTS.PAY_IS_NOT_CONTROLLER);
export const PAY_IS_APPROVER = policy(RIGHTS.PAY_IS_APPROVER);
export const PAY_IS_NOT_APPROVER = policy(RIGHTS.PAY_IS_NOT_APPROVER);

export const CAN_ACCESS_TO_PAY_SETTINGS = policy(
  [RIGHTS.PAY_ALL_DISCONNECTED_PAYMENT_PROVIDER],
  [RIGHTS.PAY_HAS_NON_DISCONNECTED_PAYMENT_PROVIDER, RIGHTS.PAY_IS_OWNER],
  [RIGHTS.PAY_HAS_NON_DISCONNECTED_PAYMENT_PROVIDER, RIGHTS.PAY_IS_CONTROLLER],
);

export const CAN_ACCESS_TO_PAY_SETTINGS_BENEFICIARIES = policy(
  [RIGHTS.PAY_HAS_CONNECTED_PAYMENT_PROVIDER, RIGHTS.PAY_IS_OWNER],
  [RIGHTS.PAY_HAS_CONNECTED_PAYMENT_PROVIDER, RIGHTS.PAY_IS_CONTROLLER],
);
